import "./ClientHistoryModal.css";
import { Appointment } from "../../../models/AppointmentModels";
import { Branch } from "../../../models/BranchModels";
import AppointmentStatusBadge from "../../../components/AppointmentStatusBadge";
import { formatDateTime } from "../../../utils/DateTime";

interface ClientHistoryModalProps {
  isOpen: boolean;
  clientName: string;
  clientPhone?: string | null;
  appointments: Appointment[];
  loading: boolean;
  error: string | null;
  branchMap: Map<string, Branch>;
  onClose: () => void;
  onRetry: () => void;
}

const serviceTotal = (appointment: Appointment): number =>
  appointment.services.reduce(
    (sum, service) => sum + service.price * service.quantity,
    0,
  );

const ClientHistoryModal = ({
  isOpen,
  clientName,
  clientPhone,
  appointments,
  loading,
  error,
  branchMap,
  onClose,
  onRetry,
}: ClientHistoryModalProps) => {
  if (!isOpen) return null;

  const grandTotal = appointments.reduce(
    (sum, appointment) => sum + serviceTotal(appointment),
    0,
  );

  return (
    <div
      className="appt-history-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Appointment history for ${clientName}`}
    >
      <div className="appt-history">
        <header className="appt-history__header">
          <div>
            <h3>{clientName}</h3>
            <p>
              {clientPhone
                ? `Appointment history · ${clientPhone}`
                : "Appointment history"}
            </p>
          </div>
          <button
            type="button"
            className="appt-history__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="appt-history__body">
          {loading ? (
            <p className="appt-history__status">Loading client history...</p>
          ) : error ? (
            <div className="appt-history__status">
              <p>{error}</p>
              <button
                type="button"
                className="appt-btn appt-btn--small"
                onClick={onRetry}
              >
                Retry
              </button>
            </div>
          ) : appointments.length === 0 ? (
            <p className="appt-history__status">
              No appointment history for this client yet.
            </p>
          ) : (
            <ul className="appt-history__list">
              {appointments.map((appointment) => (
                <li className="appt-history__item" key={appointment.id}>
                  <div className="appt-history__item-head">
                    <span className="appt-history__date">
                      {formatDateTime(appointment.scheduledAt)}
                    </span>
                    <AppointmentStatusBadge status={appointment.status} />
                  </div>
                  <div className="appt-history__item-meta">
                    <span>
                      {appointment.branchId
                        ? branchMap.get(appointment.branchId)?.name ?? "—"
                        : "—"}
                    </span>
                    <span>
                      {appointment.staffName || "—"}
                    </span>
                  </div>
                  {appointment.services.length > 0 && (
                    <p className="appt-history__services">
                      {appointment.services
                        .map((service) => service.serviceName || "Service")
                        .join(", ")}
                      <span className="appt-history__total">
                        {" "}
                        · {serviceTotal(appointment)}
                      </span>
                    </p>
                  )}
                  {appointment.notes && (
                    <p className="appt-history__notes">{appointment.notes}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {!loading && !error && appointments.length > 0 && (
          <footer className="appt-history__footer">
            <span>
              {appointments.length}{" "}
              {appointments.length === 1 ? "appointment" : "appointments"}
            </span>
            <span>Total · {grandTotal}</span>
          </footer>
        )}
      </div>
    </div>
  );
};

export default ClientHistoryModal;